<template>
  <div class="section-shell">
    <div
      class="section-brand"
      v-if="isCustomIconPanel"
    >
      <slot name="icon-panel"></slot>
    </div>
    <div
      class="section-toolbar"
      v-if="!isHideButton || $slots['toolbar-extra'] || fields.length > 0"
    >
      <div
        class="section-toolbar__actions"
        v-if="!isHideButton"
      >
        <div class="btn-group section-button-group">
          <button
            type="button"
            class="btn btn-sm btn-outline-success"
            v-bind:class="addButtonClass"
            v-bind:title="$filters.localize('button.add')"
            v-on:click="router(uuid, editPage)"
          >
            <i v-bind:class="addIcon || 'fas fa-plus'"></i>
            <span class="section-add-label section-add-label--full">{{
              $filters.localize("button.add") }}</span>
            <span class="section-add-label section-add-label--short">{{
              addShortLabel || $filters.localize("button.add") }}</span>
          </button>
          <slot name="buttons">
          </slot>
        </div>
      </div>
      <div
        class="section-toolbar__actions"
        v-else
      ></div>
      <div class="section-toolbar__meta">
        <div class="section-count-row">
          <slot
            class="col"
            name="count"
            :data="pagination"
          ></slot>
          <div class="section-count-pill">{{
            $filters.localize("main.countTitle") }}: {{ pagination.count }}
          </div>
        </div>
      </div>
      <div
        class="section-toolbar__extra"
        v-if="$slots['toolbar-extra']"
      >
        <slot name="toolbar-extra"></slot>
      </div>
      <div
        class="section-toolbar__filters"
        v-if="fields.length > 0 && isMobile"
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary section-filter-button"
          v-on:click="openFilters"
        >
          <i class="fas fa-filter"></i>
          <span>Фільтри</span>
          <span
            v-if="filters.length > 0"
            class="section-filter-button__badge"
          >{{ filters.length }}</span>
        </button>
      </div>
    </div>

    <teleport
      to="body"
      :disabled="!isMobile"
    >
      <div
        v-if="isFilterPanelVisible"
        class="section-filter-drawer"
        role="dialog"
        aria-modal="true"
        v-on:click.self="closeFilters"
      >
        <aside class="section-filter-drawer__panel">
        <div class="section-filter-drawer__header">
          <div class="section-filter-drawer__title">
            <h2>
              <span>Фільтри</span>
              <span class="section-filter-drawer__badge">{{ filters.length
                }}</span>
            </h2>
          </div>
          <form
            class="section-filter"
            @submit.prevent="addFilter(filter)"
          >
            <div class="section-filter__group input-group">
              <div
                class="section-filter__field section-filter__field--floating"
                :class="{ 'section-filter__field--filled': !!filter.field }"
              >
                <label class="section-filter__label">{{
                  $filters.localize("main.type") }}</label>
                <select
                  v-model="filter.field"
                  class="form-control section-control"
                >
                  <option
                    v-for="option in fields"
                    :key="option.field"
                    :value="option.field"
                  >
                    {{ localizeColumn(option) }}
                  </option>
                </select>
              </div>

              <div
                class="section-filter__field section-filter__field--floating"
                :class="{ 'section-filter__field--filled': !!filter.type }"
              >
                <label class="section-filter__label">{{
                  $filters.localize("button.action") }}</label>
                <select
                  :disabled="!filter.field || filter.field.length == 0"
                  v-model="filter.type"
                  class="form-control section-control"
                >
                  <option
                    v-for="option in typesFilterfilter(filter)"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>

              <div
                class="section-filter__field section-filter__field--floating"
                :class="{ 'section-filter__field--filled': filter.value !== null && filter.value !== '' }"
              >
                <label class="section-filter__label">{{
                  $filters.localize("main.value") }}</label>
                <input
                  v-if="currentInputType(filter) !== 'lookup'"
                  :type="currentInputType(filter)"
                  class="form-control text-center section-control"
                  v-model="filter.value"
                  required
                />
                <select
                  v-else
                  class="form-control section-control"
                  v-model="filter.value"
                  required
                >
                  <option
                    v-for="option in fields.find(x => x.field === filter.field).lookup"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>

              <button
                type="submit"
                :disabled="!(!!filter.value && !!filter.field && !!filter.type) && currentInputType(filter) != 'checkbox'"
                class="btn btn-primary section-filter__submit"
              >
                {{ $filters.localize("main.submitFilter") }}
              </button>
            </div>
          </form>
          <button
            type="button"
            class="section-filter-drawer__close"
            v-bind:aria-label="$filters.localize('button.close')"
            v-on:click="closeFilters"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div
          v-if="filters.length > 0 || isMobile"
          class="section-active-filters"
        >
          <div class="section-active-filters__top">
            <h3>Активні</h3>
            <button
              type="button"
              v-if="filters.length > 0"
              class="section-active-filters__clear"
              v-bind:aria-label="$filters.localize('button.filter.clear')"
              v-bind:title="$filters.localize('button.filter.clear')"
              v-on:click="clearFilters"
            >
              <i class="fas fa-eraser"></i>
            </button>
          </div>
          <div
            v-if="filters.length > 0"
            class="section-active-filters__list"
          >
            <div
              v-for="(filter, index) in filters"
              :key="index"
              class="section-filter-chip input-group"
            >
              <button
                type="button"
                class="section-filter-chip__field"
                v-bind:title="getFilterString(filter)"
                v-on:click.prevent="removeFilter(index)"
              >
                <span>{{ getFilterString(filter) }}</span>
                <i class="fas fa-times section-filter-chip__mobile-remove"></i>
              </button>
              <div
                class="section-filter-chip__control section-filter__field--floating"
                :class="{ 'section-filter__field--filled': !!filter.type }"
              >
                <label class="section-filter__label">{{
                  $filters.localize("button.action") }}</label>
                <select
                  v-model="filter.type"
                  class="form-control section-control"
                >
                  <option
                    v-for="option in typesFilterfilter(filter)"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
              <div
                class="section-filter-chip__control section-filter__field--floating"
                :class="{ 'section-filter__field--filled': filter.value !== null && filter.value !== '' }"
              >
                <label class="section-filter__label">{{
                  $filters.localize("main.value") }}</label>
                <input
                  v-if="currentInputType(filter) !== 'lookup'"
                  :type="currentInputType(filter)"
                  class="form-control text-center section-control"
                  v-model="filter.value"
                  required
                />
                <select
                  v-else
                  class="form-control section-control"
                  v-model="filter.value"
                  required
                >
                  <option
                    v-for="option in fields.find(x => x.field === filter.field).lookup"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div
            v-else
            class="section-active-filters__empty"
          >
            {{ $filters.localize("main.notdata") }}
          </div>
        </div>
        </aside>
      </div>
    </teleport>

    <div class="section-workspace">
      <div class="section-table-area">
        <div class="section-table-area__inner">
          <div class="section-table-card">
            <div
              class="section-table-scroll"
              :class="fields.length > 0 ? 'table-container-xs' : 'table-container'"
            >
              <table
                class="table table-hover section-table"
                v-bind:class="{
                  'section-table--custom-row': isCustomRow,
                  'section-table--messages': schemaName === 'message',
                  'section-table--withdrawal': schemaName === 'withdrawal',
                }"
              >
                <thead>
                  <tr>
                    <th v-if="isCustomRow"></th>
                    <th class="section-row-number">#</th>
                    <th
                      class="text-center"
                      v-bind:class="header.class ? header.class : ''"
                      v-bind:style="header.style ? header.style : null"
                      v-bind:title="header.title ? header.title : localizeColumn(header)"
                      v-for="header of headers"
                      v-bind:colspan="header.colspan ? header.colspan : ''"
                      v-bind:key="header.field"
                    >
                      <p class="d-inline">{{ localizeColumn(header) }}</p>
                      <fas-sort
                        class="d-inline"
                        v-if="header.sort"
                        @click="sort(header.field)"
                        v-bind:currentSort="currentSort"
                        v-bind:currentSortDir="currentSortDir"
                        v-bind:field="header.field"
                      />
                    </th>
                  </tr>
                </thead>
                <slot
                  name="body"
                  v-if="isCustomRow"
                  :data="rowsData"
                  :headers="headers"
                  :getRowValue="getRowValue"
                  :router="router"
                  :routerOpen="routerOpen"
                  :pageCount="(currentPage - 1) * pageSize + 1"
                >
                </slot>
                <tbody v-else>
                  <tr
                    v-for="(row, index) of rowsData"
                    v-bind:key="row.id"
                  >
                    <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                    <td
                      class="text-wrap section-table__cell"
                      v-bind:colspan="header.colspan ? header.colspan : ''"
                      v-bind:class="header.class ? header.class : ''"
                      v-bind:style="header.style ? header.style : null"
                      v-for="header of headers"
                      v-bind:key="row.id + header.field"
                      v-on:click.middle.prevent="routerOpen(getRowValue(row, header.router), header.router?.name)"
                      v-on:click.prevent="router(getRowValue(row, header.router), header.router?.name)"
                    >
                      <slot
                        name="cell"
                        :row="row"
                        :header="header"
                        :value="getRowValue(row, header)"
                        :getRowValue="getRowValue"
                      >
                        <span
                          v-if="isWithdrawalStatusColumn(header)"
                          class="withdrawal-status-chip"
                          v-bind:class="getWithdrawalStatusClass(getRowValue(row, header))"
                        >
                          {{ getRowValue(row, header) }}
                        </span>
                        <template v-else>
                          {{ getRowValue(row, header) }}
                        </template>
                      </slot>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                v-if="this.pagination.entities?.length == 0"
                class="section-empty"
              >
                {{ $filters.localize("main.notdata") }}
              </div>
            </div>

            <div
              v-if="loading"
              class="loader-overlay"
            >
              <div
                class="spinner-border text-primary"
                role="status"
              >
              </div>
            </div>
          </div>
          <div class="section-pagination">
            <nav
              class="section-pagination__nav"
              v-if="pagination.entities?.length != 0"
            >
              <ul class="pagination section-pagination__list">
                <li
                  class="page-item"
                  v-if="currentPage - 1 > 0"
                >
                  <a
                    class="page-link"
                    v-on:click="backPage"
                    aria-label="Предидущая"
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </a>
                </li>
                <li
                  class="page-item"
                  v-if="currentPage - 1 > 0"
                ><a
                    class="page-link"
                    v-on:click="backPage"
                  >{{ currentPage - 1 }}</a></li>
                <li class="page-item active"><a
                    class="page-link"
                    aria-current="page"
                  >{{ currentPage }}</a>
                </li>
                <li
                  class="page-item"
                  v-if="pagination.totalPages > currentPage"
                ><a
                    class="page-link"
                    v-on:click="nextPage"
                  >{{ currentPage + 1 }}</a></li>
                <li
                  class="page-item"
                  v-if="pagination.totalPages > currentPage"
                >
                  <a
                    class="page-link"
                    v-on:click="nextPage"
                    aria-label="Следующая"
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </a>
                </li>
              </ul>
            </nav>
            <div class="section-pagination__summary">{{ (pagination.packageSize
              *
              (currentPage - 1) + 1) ?? 0 }} - {{
                (pagination.packageSize *
                  currentPage - (pagination.packageSize -
                    pagination.entities.length)) ?? 9 }}
              із {{ pagination.count ?? 0 }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import fasSort from '@/components/Sections/FasSort.vue';
import table from '@/mixins/table.js';
export default {
  name: "TSection",
  mixins: [table],
  props: ["isHideButton", "isCustomRow", "isCustomIconPanel", "addShortLabel", "addIcon", "addButtonClass"],
  components: {
    fasSort
  },
  data() {
    return {
      uuid: uuidv4(),
      showFilter: false,
      isMobile: false,
    }
  },
  computed: {
    editPage: {
      get() {
        return this.schemaName.charAt(0).toUpperCase() + this.schemaName.slice(1) + 'Page';
      }
    },
    pageSize: {
      get() {
        return this.pagination.packageSize;
      }
    },
    isFilterPanelVisible: {
      get() {
        return this.fields.length > 0 && (!this.isMobile || this.showFilter);
      }
    }
  },
  watch: {
    fields() {
      if (!this.fields?.length) {
        this.showFilter = false;
      }
    }
  },
  methods: {
    openFilters() {
      this.showFilter = true;
    },
    closeFilters() {
      this.showFilter = false;
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 767.98;
    },
    typesFilterfilter(filter) {
      if (filter.field) {
        var type = this.fields.find(x => x.field == filter.field)?.type ?? "all";
        return this.filterTypes.filter(x => x.type == "all" || x.type.includes(type));
      }
      return [];
    },
    currentInputType(filter) {
      if (filter.field) {
        var type = this.fields.find(x => x.field == filter.field)?.type ?? "all";
        return type == "all" ? "text" : type;
      }
      return "text";
    },
    isWithdrawalStatusColumn(header) {
      return this.schemaName === "withdrawal" && header.field === "status.name";
    },
    getWithdrawalStatusClass(status) {
      const normalized = (status ?? "").toString().trim().toLowerCase();
      if (normalized.includes("виплач")) {
        return "withdrawal-status-chip--paid";
      }
      if (normalized.includes("скас")) {
        return "withdrawal-status-chip--canceled";
      }
      if (normalized.includes("очіку")) {
        return "withdrawal-status-chip--wait";
      }
      return "withdrawal-status-chip--default";
    }
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
    this.loadRows();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateViewport);
  },
}
</script>

